import React, { useState, useEffect } from 'react';
import '../styles/Leaderboard.css';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Leaderboard = ({ onClose, currentUserId }) => {
  const [activeTab, setActiveTab] = useState('season');
  const [seasonData, setSeasonData] = useState(null);
  const [championsData, setChampionsData] = useState(null);
  const [fallenKingsData, setFallenKingsData] = useState(null);
  const [tournamentLeaderboard, setTournamentLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState('all');

  const variants = [
    { id: 'all', name: 'All Variants' },
    { id: 'standard', name: 'Classic' },
    { id: 'instant_king', name: 'Instant King' },
    { id: 'fight_to_be_king', name: 'Fight to be King' },
    { id: 'players_choice', name: "Player's Choice" },
    { id: 'pawns_wild', name: "Pawn's Wild" },
    { id: 'kings_court_2dukes', name: 'Two Dukes' },
    { id: 'handicap_mode', name: 'Handicap' },
  ];

  useEffect(() => {
    fetchLeaderboardData();
  }, [activeTab, selectedVariant]);

  const fetchLeaderboardData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'season') {
        const response = await fetch(`${API_URL}/api/leaderboard/season?variant=${selectedVariant}`);
        const data = await response.json();
        if (data.success) {
          setSeasonData(data);
        }
      } else if (activeTab === 'champions') {
        const response = await fetch(`${API_URL}/api/leaderboard/champions`);
        const data = await response.json();
        if (data.success) {
          setChampionsData(data);
        }
      } else if (activeTab === 'fallen') {
        const response = await fetch(`${API_URL}/api/leaderboard/fallen-kings`);
        const data = await response.json();
        if (data.success) {
          setFallenKingsData(data);
        }
      } else if (activeTab === 'tournaments') {
        const response = await fetch(`${API_URL}/api/tournaments/leaderboard`);
        const data = await response.json();
        if (data.success) {
          setTournamentLeaderboard(data.leaderboard || []);
        }
      }
    } catch (error) {
      logger.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return '👑';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const renderSeasonLeaderboard = () => {
    if (!seasonData) return null;

    return (
      <div className="leaderboard-section">
        <div className="season-header">
          <h3>🏆 Weekly Rankings</h3>
          <p className="week-info">Week of {formatDate(seasonData.week_start)}</p>
        </div>

        <div className="variant-filter">
          {variants.map(variant => (
            <button
              key={variant.id}
              className={`variant-btn ${selectedVariant === variant.id ? 'active' : ''}`}
              onClick={() => setSelectedVariant(variant.id)}
            >
              {variant.name}
            </button>
          ))}
        </div>

        {seasonData.leaderboard.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🎮</span>
            <p>No games played this week yet</p>
            <p className="hint">Be the first to claim the top spot!</p>
          </div>
        ) : (
          <div className="leaderboard-list">
            {seasonData.leaderboard.map((player, index) => (
              <div 
                key={player.user_id} 
                className={`leaderboard-row ${index < 3 ? 'top-three' : ''} ${player.user_id === currentUserId ? 'current-user' : ''}`}
              >
                <div className="rank-badge">
                  {getRankBadge(player.rank)}
                </div>
                <div className="player-avatar">
                  {player.avatar || '👤'}
                </div>
                <div className="player-info">
                  <span className="player-name">{player.name}</span>
                  {player.user_id === currentUserId && <span className="you-badge">YOU</span>}
                </div>
                <div className="player-stats">
                  <div className="stat-item wins">
                    <span className="stat-value">{player.wins}</span>
                    <span className="stat-label">Wins</span>
                  </div>
                  <div className="stat-item games">
                    <span className="stat-value">{player.games_played}</span>
                    <span className="stat-label">Games</span>
                  </div>
                  <div className="stat-item winrate">
                    <span className="stat-value">
                      {player.games_played > 0 
                        ? Math.round((player.wins / player.games_played) * 100) 
                        : 0}%
                    </span>
                    <span className="stat-label">Win Rate</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderChampions = () => {
    if (!championsData) return null;

    const { king_of_kingdom, variant_champions, variant_dukes } = championsData;

    const variantIcons = {
      standard: '♟️',
      instant_king: '⚡',
      fight_to_be_king: '⚔️',
      players_choice: '🎯',
      pawns_wild: '♙',
      kings_court_2dukes: '🛡️',
      handicap_mode: '⚖️',
    };

    const variantNames = {
      standard: 'Classic',
      instant_king: 'Instant King',
      fight_to_be_king: 'Fight to be King',
      players_choice: "Player's Choice",
      pawns_wild: "Pawn's Wild",
      kings_court_2dukes: 'Two Dukes',
      handicap_mode: 'Handicap',
    };

    return (
      <div className="leaderboard-section champions-section">
        {/* King of the Kingdom */}
        <div className="king-section">
          <h3 data-testid="king-section-title">👑 King of Chess Kingdom</h3>
          <p className="king-subtitle">The player who holds #1 in ALL variants simultaneously</p>
          
          {king_of_kingdom ? (
            <div className="king-card" data-testid="king-card">
              <div className="king-crown">👑</div>
              <div className="king-avatar">{king_of_kingdom.avatar || '👤'}</div>
              <div className="king-name">{king_of_kingdom.name}</div>
              <div className="king-stats">
                <div className="king-stat">
                  <span className="stat-value">{king_of_kingdom.total_wins}</span>
                  <span className="stat-label">Total Wins</span>
                </div>
                <div className="king-stat">
                  <span className="stat-value">{king_of_kingdom.days_as_king || 0}</span>
                  <span className="stat-label">Days as King</span>
                </div>
                <div className="king-stat">
                  <span className="stat-value">{king_of_kingdom.variants_dominated || 0}</span>
                  <span className="stat-label">Variants Led</span>
                </div>
              </div>
              <div className="king-crowned">
                Crowned: {formatDate(king_of_kingdom.crowned_at)}
              </div>
            </div>
          ) : (
            <div className="empty-state king-empty" data-testid="king-vacant">
              <span className="empty-icon">🏰</span>
              <p>The throne is vacant!</p>
              <p className="hint">Become #1 in all variants to claim the crown</p>
            </div>
          )}
        </div>

        {/* Variant Champions & Dukes */}
        <div className="variant-champions">
          <h3>🎖️ Variant Champions & Dukes</h3>
          <p className="champions-subtitle">Champions (#1) and Dukes (#2) in each game variant</p>
          
          <div className="champions-grid" data-testid="champions-grid">
            {Object.entries(variant_champions).map(([variant, champion]) => {
              const duke = variant_dukes ? variant_dukes[variant] : null;
              return (
                <div key={variant} className="champion-card" data-testid={`champion-card-${variant}`}>
                  <div className="champion-variant">
                    {variantIcons[variant] || '🎮'} {variantNames[variant] || variant}
                  </div>
                  {champion ? (
                    <div className="champion-info">
                      <div className="champion-rank-label">👑 Champion</div>
                      <div className="champion-avatar">{champion.avatar || '👤'}</div>
                      <div className="champion-name">{champion.name}</div>
                      <div className="champion-wins">{champion.total_wins} wins</div>
                    </div>
                  ) : (
                    <div className="champion-empty">
                      <span>No champion yet</span>
                    </div>
                  )}
                  {duke ? (
                    <div className="duke-info" data-testid={`duke-card-${variant}`}>
                      <div className="duke-rank-label">🛡️ Duke</div>
                      <div className="duke-avatar">{duke.avatar || '👤'}</div>
                      <div className="duke-name">{duke.name}</div>
                      <div className="duke-wins">{duke.total_wins} wins</div>
                    </div>
                  ) : (
                    <div className="duke-empty">
                      <span className="duke-rank-label">🛡️ Duke</span>
                      <span>No duke yet</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderFallenKings = () => {
    if (!fallenKingsData) return null;

    return (
      <div className="leaderboard-section fallen-section">
        <div className="fallen-header">
          <h3>⚰️ Fallen Kings</h3>
          <p className="fallen-subtitle">Legends who once wore the crown</p>
        </div>

        {fallenKingsData.fallen_kings.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📜</span>
            <p>No fallen kings yet</p>
            <p className="hint">The first King hasn&apos;t been dethroned yet</p>
          </div>
        ) : (
          <div className="fallen-list">
            {fallenKingsData.fallen_kings.map((king, index) => (
              <div key={`fallen-${king.name}-${king.fallen_at}`} className="fallen-card">
                <div className="fallen-rank">#{index + 1}</div>
                <div className="fallen-avatar">{king.avatar || '👤'}</div>
                <div className="fallen-info">
                  <div className="fallen-name">{king.name}</div>
                  <div className="fallen-reign">
                    Reigned for <strong>{king.reign_days}</strong> days
                  </div>
                  <div className="fallen-period">
                    {formatDate(king.crowned_at)} - {formatDate(king.fallen_at)}
                  </div>
                </div>
                <div className="fallen-stats">
                  <span className="fallen-wins">{king.total_wins_at_crown} wins at crown</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTournamentLeaderboard = () => {
    if (!tournamentLeaderboard) return null;

    return (
      <div className="leaderboard-section" data-testid="tournament-leaderboard">
        <h3>🏅 Tournament Leaderboard</h3>
        <p className="champions-subtitle">Top tournament winners across all events</p>

        {tournamentLeaderboard.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🏅</span>
            <p>No tournament champions yet</p>
            <p className="hint">Win tournaments to appear here</p>
          </div>
        ) : (
          <div className="leaderboard-table">
            <div className="table-header">
              <span className="col-rank">#</span>
              <span className="col-player">Player</span>
              <span className="col-stat">Wins</span>
              <span className="col-stat">Played</span>
              <span className="col-stat">Badges</span>
            </div>
            {tournamentLeaderboard.map((player, idx) => (
              <div
                key={player.user_id}
                className={`table-row ${currentUserId === player.user_id ? 'current-user' : ''}`}
                data-testid={`tournament-lb-row-${idx}`}
              >
                <span className="col-rank">{getRankBadge(player.rank)}</span>
                <span className="col-player">
                  <span className="player-avatar">{player.avatar || '👤'}</span>
                  {player.name}
                </span>
                <span className="col-stat highlight">{player.tournament_wins}</span>
                <span className="col-stat">{player.tournaments_participated}</span>
                <span className="col-stat">{player.badges_count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="leaderboard-overlay" data-testid="leaderboard-modal">
      <div className="leaderboard-modal">
        <button 
          className="close-btn" 
          onClick={onClose}
          data-testid="leaderboard-close-btn"
        >
          ×
        </button>
        
        <div className="leaderboard-header">
          <h2>🏆 Leaderboards</h2>
        </div>

        <div className="leaderboard-tabs">
          <button 
            className={`tab-btn ${activeTab === 'season' ? 'active' : ''}`}
            onClick={() => setActiveTab('season')}
            data-testid="season-tab"
          >
            📊 Weekly
          </button>
          <button 
            className={`tab-btn ${activeTab === 'champions' ? 'active' : ''}`}
            onClick={() => setActiveTab('champions')}
            data-testid="champions-tab"
          >
            👑 King & Champions
          </button>
          <button 
            className={`tab-btn ${activeTab === 'fallen' ? 'active' : ''}`}
            onClick={() => setActiveTab('fallen')}
            data-testid="fallen-tab"
          >
            ⚰️ Fallen Kings
          </button>
          <button 
            className={`tab-btn ${activeTab === 'tournaments' ? 'active' : ''}`}
            onClick={() => setActiveTab('tournaments')}
            data-testid="tournaments-tab"
          >
            🏅 Tournaments
          </button>
        </div>

        <div className="leaderboard-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading leaderboard...</p>
            </div>
          ) : (
            <>
              {activeTab === 'season' && renderSeasonLeaderboard()}
              {activeTab === 'champions' && renderChampions()}
              {activeTab === 'fallen' && renderFallenKings()}
              {activeTab === 'tournaments' && renderTournamentLeaderboard()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
